import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Dropdown } from 'react-bootstrap';
import FavoriteDropdown from '../Global/FavoriteDropdown';
import { getSalesReps, getCarriers, getIncidentActions, getIncidentTypes, getProductCategories, getClientOrigins, getPaymentMethods, logError } from '../../api';
import DatePicker from 'react-datepicker';
import { format, startOfToday, startOfWeek, endOfWeek, subDays, addDays } from 'date-fns';
import { formatDateToYYYYMMDD } from '../../utils/DateUtils';
import { isComercialAndNotOwnerAdminGestion, isJefeComerciales, isPropietarioOrAdminOrGestionOrEspectador, isTransportistaAndNotOwnerAdminGestion, isJefeTransportistas, isComercial, isLeads } from '../../services/AuthService';
import ClientSearchModal from '../Global/ClientSearchModal';

const OrderListFilter = ({ filters, setFilters, type, onSearch, initialFilter = null, clientOrigins = [], paymentMethods = [], carriers = [] }) => {
  const [salesReps, setSalesReps] = useState([]);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [incidentActions, setIncidentActions] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(true);

  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [localFilters, setLocalFilters] = useState(() => ({
        ...filters,
        isImportant: filters.isImportant ?? null,
        hasSpecialProducts: filters.hasSpecialProducts ?? null,
        zipCode: Array.isArray(filters.zipCode) ? filters.zipCode.join(', ') : (filters.zipCode || '')
  }));
  const [isInitialized, setIsInitialized] = useState(false);
  const searchTimeoutRef = useRef(null);


  const fetchData = async () => {
    try {
      const [incidentTypesData, incidentActionsData, productCategoriesData] = await Promise.all([
        getIncidentTypes(),
        getIncidentActions(),
        getProductCategories()
      ]);

      setIncidentTypes(incidentTypesData);
      setIncidentActions(incidentActionsData);
      setProductCategories(productCategoriesData);

      if(!isComercialAndNotOwnerAdminGestion() && (!isTransportistaAndNotOwnerAdminGestion() || isJefeComerciales() || isLeads()))
      {
        const salesRepsData = await getSalesReps()
  
        setSalesReps(salesRepsData);
      }



    } catch (error) {
      logError(error);
      console.error('Error fetching dropdown data:', error);
    }
  };

  useEffect(() => {
    fetchData();
    setIsInitialized(true);
  }, [type]);

  useEffect(() => {
    if (initialFilter && Object.values(filters).every(value => value === '')) {
      setFilters(initialFilter);
    }
  }, []);

  useEffect(() => {
    if (type === 'transport') {
      if(!filters)
      {
        resetFilters();
      }
    }
  }, [type, filters, setFilters]);

  const handleDateChange = (field, value) => {
    const formattedDate = value ? format(value, 'yyyy-MM-dd') : '';
    setLocalFilters((prevFilters) => {
      const newFilters = { ...prevFilters, [field]: formattedDate };

      if (field === 'orderDateFrom') {
        if (newFilters.orderDateTo && newFilters.orderDateTo < formattedDate) {
          newFilters.orderDateTo = formattedDate;
        }
      } else if (field === 'orderDateTo') {
        if (newFilters.orderDateFrom && newFilters.orderDateFrom > formattedDate) {
          newFilters.orderDateFrom = formattedDate;
        }
      } else if (field === 'deliveryDateFrom') {
        if (newFilters.deliveryDateTo && newFilters.deliveryDateTo < formattedDate) {
          newFilters.deliveryDateTo = formattedDate;
        }
      } else if (field === 'deliveryDateTo') {
        if (newFilters.deliveryDateFrom && newFilters.deliveryDateFrom > formattedDate) {
          newFilters.deliveryDateFrom = formattedDate;
        }
      }

      return newFilters;
    });
  };

  const resetFilters = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
  
    const baseFilters = {
      orderDateFrom: '',
      orderDateTo: '',
      carrierId: [],
      salesRepId: [],
      incidentTypeId: '',
      incidentActionId: '',
      clientId: '',
      status: '',
      hasSpecialProducts: null,
      zipCode: '',
      city: '',
      productCategoryId: '',
      clientOriginId: '',
      paymentMethodId: '',
      isImportant: null,
    };
  
    if (type === 'transport' || type === 'calendar') {
      baseFilters.deliveryDateFrom = formatDateToYYYYMMDD(tomorrow);
      baseFilters.deliveryDateTo = formatDateToYYYYMMDD(tomorrow);
    }
  
    setLocalFilters(baseFilters);
    setSelectedClient(null);
  };
  

  const applyPredefinedFilter = (filterType) => {
    const today = startOfToday();
    const yesterday = subDays(today, 1);
    const tomorrow = addDays(today, 1);
    const startOfThisWeek = startOfWeek(today, { weekStartsOn: 1 });
    const endOfThisWeek = endOfWeek(today, { weekStartsOn: 1 });

    switch (filterType) {
      case 'yesterday':
        setLocalFilters({
          ...localFilters,
          orderDateFrom: format(yesterday, 'yyyy-MM-dd'),
          orderDateTo: format(yesterday, 'yyyy-MM-dd'),
          deliveryDateFrom: '',
          deliveryDateTo: '',
          carrierId: [],
          incidentTypeId: '',
          incidentActionId: '',
        });
        break;
      case 'today':
        setLocalFilters({
          ...localFilters,
          orderDateFrom: format(today, 'yyyy-MM-dd'),
          orderDateTo: format(today, 'yyyy-MM-dd'),
          deliveryDateFrom: '',
          deliveryDateTo: '',
          carrierId: [],
          incidentTypeId: '',
          incidentActionId: '',
        });
        break;
      case 'thisWeek':
        setLocalFilters({
          ...localFilters,
          orderDateFrom: format(startOfThisWeek, 'yyyy-MM-dd'),
          orderDateTo: format(endOfThisWeek, 'yyyy-MM-dd'),
          deliveryDateFrom: '',
          deliveryDateTo: '',
          carrierId: [],
          incidentTypeId: '',
          incidentActionId: '',
        });
        break;
      case 'deliveryToday':
        setLocalFilters({
          ...localFilters,
          deliveryDateFrom: format(today, 'yyyy-MM-dd'),
          deliveryDateTo: format(today, 'yyyy-MM-dd'),
          orderDateFrom: '',
          orderDateTo: '',
          carrierId: [],
          incidentTypeId: '',
          incidentActionId: '',
        });
        break;
      
      case 'deliveryTomorrow':
        setLocalFilters({
          ...localFilters,
          deliveryDateFrom: format(tomorrow, 'yyyy-MM-dd'),
          deliveryDateTo: format(tomorrow, 'yyyy-MM-dd'),
          orderDateFrom: '',
          orderDateTo: '',
          carrierId: [],
          incidentTypeId: '',
          incidentActionId: '',
        });
        break;
        
      case 'withoutCarrier':
        setLocalFilters({
          ...localFilters,
          carrierId: ['0'],
          orderDateFrom: '',
          orderDateTo: '',
          deliveryDateFrom: '',
          deliveryDateTo: '',
          incidentTypeId: '',
          incidentActionId: '',
        });
        break;

        case 'deliveryThisWeek': {
          setLocalFilters({
            ...localFilters,
            deliveryDateFrom: format(startOfThisWeek, 'yyyy-MM-dd'),
            deliveryDateTo: format(endOfThisWeek, 'yyyy-MM-dd'),
            orderDateFrom: '',
            orderDateTo: '',
            carrierId: [],
            incidentTypeId: '',
            incidentActionId: '',
          });
          break;
        }

        case 'deliveryNextWeek': {
          const nextWeekStart = addDays(endOfThisWeek, 1);
          const nextWeekEnd = addDays(endOfThisWeek, 7);
          setLocalFilters({
            ...localFilters,
            deliveryDateFrom: format(nextWeekStart, 'yyyy-MM-dd'),
            deliveryDateTo: format(nextWeekEnd, 'yyyy-MM-dd'),
            orderDateFrom: '',
            orderDateTo: '',
            carrierId: [],
            incidentTypeId: '',
            incidentActionId: '',
          });
          break;
        }

        case 'deliveryNext7': {
          const next7 = addDays(today, 7);
          setLocalFilters({
            ...localFilters,
            deliveryDateFrom: format(today, 'yyyy-MM-dd'),
            deliveryDateTo: format(next7, 'yyyy-MM-dd'),
            orderDateFrom: '',
            orderDateTo: '',
            carrierId: [],
            incidentTypeId: '',
            incidentActionId: '',
          });
          break;
        }
      default:
        break;
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setShowClientModal(false);
    setLocalFilters({
      ...localFilters,
      clientId: client.id
    });
  };

  const lastFiltersRef = useRef(JSON.stringify({
    ...filters,
    zipCode: Array.isArray(filters.zipCode) ? filters.zipCode.join(', ') : (filters.zipCode || '')
  }));

  useEffect(() => {
    if (!isInitialized) return;
    
    const currentFilters = JSON.stringify(localFilters);
    if (currentFilters === lastFiltersRef.current) return;
    
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    lastFiltersRef.current = currentFilters;
    searchTimeoutRef.current = setTimeout(() => {
      const processedFilters = {
        ...localFilters,
        zipCode: localFilters.zipCode 
          ? localFilters.zipCode.split(',').map(code => code.trim()).filter(code => code)
          : []
      };
      setFilters(processedFilters);
    }, 500);
    
    return () => clearTimeout(searchTimeoutRef.current);
  }, [localFilters, setFilters, isInitialized]);

  return (
    <div className="mb-4" data-testid="order-list-filter">
      <div className="mt-3 d-flex flex-wrap gap-2">
        <Button variant="info" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-button">
          {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
        </Button>
        <Button variant="info" onClick={resetFilters} data-testid="reset-filters-button">
          Reiniciar Filtros
        </Button>
  
        {(isPropietarioOrAdminOrGestionOrEspectador() || isComercial()) && type !== 'calendar' && (
            <Button variant="primary" onClick={() => setShowClientModal(true)} data-testid="select-client-button">
              Seleccionar Cliente
            </Button>
        )}
      </div>
  
      {selectedClient && (
        <div className="mt-2" data-testid="selected-client-display">
          <strong>Cliente seleccionado:</strong>{' '}
          {selectedClient.name} {selectedClient.lastName1} (ID: {selectedClient.id})
        </div>
      )}
  
      <ClientSearchModal
        show={showClientModal}
        onHide={() => setShowClientModal(false)}
        searchCriteria={filters}
        onClientSelect={handleClientSelect}
      />
  
      {(type !== 'calendar' || showFilters) && (
        <div className="mt-3 d-flex flex-wrap gap-2" data-testid="predefined-filters">
          <Button variant="outline-primary" onClick={() => applyPredefinedFilter('yesterday')} data-testid="filter-yesterday">
            Pedidos de ayer
          </Button>
          <Button variant="outline-primary" onClick={() => applyPredefinedFilter('today')} data-testid="filter-today">
            Pedidos de hoy
          </Button>
          <Button variant="outline-primary" onClick={() => applyPredefinedFilter('thisWeek')} data-testid="filter-this-week">
            Pedidos de esta semana
          </Button>
          <Button variant="outline-primary" onClick={() => applyPredefinedFilter('deliveryToday')} data-testid="filter-delivery-today">
            Entrega hoy
          </Button>
          <Button variant="outline-primary" onClick={() => applyPredefinedFilter('deliveryTomorrow')} data-testid="filter-delivery-tomorrow">
            Entrega mañana
          </Button>
          <Button variant="outline-primary" onClick={() => applyPredefinedFilter('deliveryThisWeek')} data-testid="filter-delivery-this-week">
            Entrega esta semana
          </Button>
          <Button variant="outline-primary" onClick={() => applyPredefinedFilter('deliveryNextWeek')} data-testid="filter-delivery-next-week">
            Entrega semana siguiente
          </Button>
          <Button variant="outline-primary" onClick={() => applyPredefinedFilter('deliveryNext7')} data-testid="filter-delivery-next-7">
            Entrega próximos 7 días
          </Button>
          {isPropietarioOrAdminOrGestionOrEspectador() && (
            <Button variant="outline-primary" onClick={() => applyPredefinedFilter('withoutCarrier')} data-testid="filter-without-carrier">
              Pedidos sin transportista
            </Button>
          )}
        </div>
      )}
  
      {showFilters && (
        <div className="d-flex flex-wrap gap-3 mt-3" data-testid="detailed-filters">
          <Form.Group>
            <Form.Label>Fecha de pedido (Desde)</Form.Label>
            <DatePicker
              selected={localFilters.orderDateFrom ? new Date(localFilters.orderDateFrom) : null}
              onChange={(date) => handleDateChange('orderDateFrom', date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="Seleccionar fecha"
              className="form-control"
              todayButton="Hoy"
              showPopperArrow={false}
              data-testid="order-date-from-picker"
            />
          </Form.Group>
  
          <Form.Group>
            <Form.Label>Fecha de pedido (Hasta)</Form.Label>
            <DatePicker
              selected={localFilters.orderDateTo ? new Date(localFilters.orderDateTo) : null}
              onChange={(date) => handleDateChange('orderDateTo', date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="Seleccionar fecha"
              className="form-control"
              todayButton="Hoy"
              showPopperArrow={false}
            />
          </Form.Group>
  
          <Form.Group>
            <Form.Label>Fecha de entrega (Desde)</Form.Label>
            <DatePicker
              selected={localFilters.deliveryDateFrom ? new Date(localFilters.deliveryDateFrom) : null}
              onChange={(date) => handleDateChange('deliveryDateFrom', date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="Seleccionar fecha"
              className="form-control"
              todayButton="Hoy"
              showPopperArrow={false}
            />
          </Form.Group>
  
          <Form.Group>
            <Form.Label>Fecha de entrega (Hasta)</Form.Label>
            <DatePicker
              selected={localFilters.deliveryDateTo ? new Date(localFilters.deliveryDateTo) : null}
              onChange={(date) => handleDateChange('deliveryDateTo', date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="Seleccionar fecha"
              className="form-control"
              todayButton="Hoy"
              showPopperArrow={false}
            />
          </Form.Group>
  
          {!isComercialAndNotOwnerAdminGestion() && (!isTransportistaAndNotOwnerAdminGestion() || isJefeTransportistas()) && (
            <Form.Group>
              <Form.Label>Transportista</Form.Label>
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" className="w-100 text-start">
                  {localFilters.carrierId.length === 0 ? 'Todos los transportistas' : `${localFilters.carrierId.length} seleccionados`}
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ maxHeight: '400px', overflowY: 'auto', width: 'max-content' }}>
                  {isPropietarioOrAdminOrGestionOrEspectador() && (
                    <Dropdown.Item as="div" onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        id="carrier-0"
                        label="Pendiente de asignación"
                        checked={localFilters.carrierId.includes('0')}
                        onChange={(e) => {
                          const newCarrierIds = e.target.checked
                            ? [...localFilters.carrierId, '0']
                            : localFilters.carrierId.filter(id => id !== '0');
                          setLocalFilters({ ...localFilters, carrierId: newCarrierIds });
                        }}
                      />
                    </Dropdown.Item>
                  )}
                  {carriers.map(carrier => (
                    <Dropdown.Item key={carrier.id} as="div" onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        id={`carrier-${carrier.id}`}
                        label={`${carrier.firstName} ${carrier.lastName1}`}
                        checked={localFilters.carrierId.includes(carrier.id.toString())}
                        onChange={(e) => {
                          const carrierId = carrier.id.toString();
                          const newCarrierIds = e.target.checked
                            ? [...localFilters.carrierId, carrierId]
                            : localFilters.carrierId.filter(id => id !== carrierId);
                          setLocalFilters({ ...localFilters, carrierId: newCarrierIds });
                        }}
                      />
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
          )}
  
          {(isPropietarioOrAdminOrGestionOrEspectador() || isJefeComerciales() || isLeads()) && (
            <Form.Group>
              <Form.Label>Comercial</Form.Label>
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" className="w-100 text-start">
                  {localFilters.salesRepId.length === 0 ? 'Todos los comerciales' : `${localFilters.salesRepId.length} seleccionados`}
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ maxHeight: '400px', overflowY: 'auto', width: 'max-content' }}>
                  {salesReps.map(rep => (
                    <Dropdown.Item key={rep.id} as="div" onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        id={`salesrep-${rep.id}`}
                        label={`${rep.name} ${rep.lastName1}`}
                        checked={localFilters.salesRepId.includes(rep.id.toString())}
                        onChange={(e) => {
                          const salesRepId = rep.id.toString();
                          const newSalesRepIds = e.target.checked
                            ? [...localFilters.salesRepId, salesRepId]
                            : localFilters.salesRepId.filter(id => id !== salesRepId);
                          setLocalFilters({ ...localFilters, salesRepId: newSalesRepIds });
                        }}
                      />
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
          )}
  
          <Form.Group>
            <Form.Label>Tipo de Incidencia</Form.Label>
            <FavoriteDropdown
              value={localFilters.incidentTypeId || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, incidentTypeId: e.target.value })}
              options={incidentTypes.map(incident => ({
                id: incident.id,
                name: incident.name,
                isFavorite: false
              }))}
              placeholder="Seleccionar Tipo de Incidencia"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Acción de Incidencia</Form.Label>
            <FavoriteDropdown
              value={localFilters.incidentActionId || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, incidentActionId: e.target.value })}
              options={incidentActions.map(action => ({
                id: action.id,
                name: action.name,
                isFavorite: false
              }))}
              placeholder="Seleccionar Acción de Incidencia"
            />
          </Form.Group>
          
          <Form.Group>
            <Form.Label>Estado del Pedido</Form.Label>
            <FavoriteDropdown
              value={localFilters.status || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, status: e.target.value })}
              options={[
                { id: 'INCIDENCIA', name: 'INCIDENCIA', isFavorite: false },
                { id: 'Entregado', name: 'Entregado', isFavorite: false },
                { id: 'Albarán firmado', name: 'Albarán firmado', isFavorite: false },
                { id: 'Asignar transportista', name: 'Asignar transportista', isFavorite: false },
                { id: 'En Reparto', name: 'En Reparto', isFavorite: false },
                { id: 'Esperando entrega', name: 'Esperando entrega', isFavorite: false },
                { id: 'Asignar fecha de entrega', name: 'Asignar fecha de entrega', isFavorite: false }
              ]}
              placeholder="Todos los estados"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Productos Especiales</Form.Label>
            <FavoriteDropdown
              value={localFilters.hasSpecialProducts === null ? '' : localFilters.hasSpecialProducts.toString()}
              onChange={(e) => setLocalFilters({ 
                ...localFilters, 
                hasSpecialProducts: e.target.value === '' ? null : e.target.value === 'true'
              })}
              options={[
                { id: 'true', name: 'Con productos especiales', isFavorite: false },
                { id: 'false', name: 'Sin productos especiales', isFavorite: false }
              ]}
              placeholder="Todos los pedidos"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>CP (Separados por coma)</Form.Label>
            <Form.Control
              type="text"
              value={localFilters.zipCode || ''}
              onChange={(e) => {
                const value = e.target.value;
                setLocalFilters({ ...localFilters, zipCode: value });
              }}
              placeholder="Códigos postales separados por comas (ej: 28001, 28002)"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Ciudad</Form.Label>
            <Form.Control
              type="text"
              value={localFilters.city || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, city: e.target.value })}
              placeholder="Buscar por ciudad"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Categoría de Producto</Form.Label>
            <FavoriteDropdown
              value={localFilters.productCategoryId || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, productCategoryId: e.target.value })}
              options={productCategories.map(category => ({
                id: category.id,
                name: category.categoryName,
                isFavorite: false
              }))}
              placeholder="Todas las categorías"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Origen del Cliente</Form.Label>
            <FavoriteDropdown
              value={localFilters.clientOriginId || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, clientOriginId: e.target.value })}
              options={clientOrigins.map(origin => ({
                id: origin.valueId,
                name: origin.name,
                isFavorite: false
              }))}
              placeholder="Todos los orígenes"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Método de Pago</Form.Label>
            <FavoriteDropdown
              value={localFilters.paymentMethodId || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, paymentMethodId: e.target.value })}
              options={paymentMethods.map(method => ({
                id: method.valueId,
                name: method.name,
                isFavorite: false
              }))}
              placeholder="Todos los métodos"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Pedidos Importantes</Form.Label>
            <FavoriteDropdown
              value={localFilters.isImportant === null ? '' : localFilters.isImportant.toString()}
              onChange={(e) => setLocalFilters({ 
                ...localFilters, 
                isImportant: e.target.value === '' ? null : e.target.value === 'true'
              })}
              options={[
                { id: 'true', name: '⭐ Solo pedidos importantes', isFavorite: false },
                { id: 'false', name: 'Solo pedidos normales', isFavorite: false }
              ]}
              placeholder="Todos los pedidos"
            />
          </Form.Group>
        </div>
      )}
    </div>
  );
  
};

export default OrderListFilter;
